import {
  Box,
  Button,
  Container,
  FormField,
  Input,
  SpaceBetween,
  Spinner,
  Header,
  ColumnLayout,
  ExpandableSection,
} from '@amzn/awsui-components-react';
import { get } from 'lodash';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useIndexInfoQuery } from './hooks/useIndexInfoQuery';
import { CodeViewer } from '../Components/Common/CodeViewer';

const INDEX_INFO_KEYS = [
  'IndexName', 'IndexState', 'IndexType', 'AutoRefresh', 'CreateTime', 'LastUpdatedTime',
];

const ValueDisplay = ({ value }: { value: any }) => {
  if (value === null || value === undefined || value === '') return <div>N/A</div>;
  if (typeof value === 'object') return <Box variant="code" fontSize="body-s">{JSON.stringify(value)}</Box>;
  const str = String(value);
  if (str.includes('arn:') || str.includes('https://') || str.includes('s3://') || str.length > 60) {
    return <Box variant="code" fontSize="body-s">{str}</Box>;
  }
  return <div>{str}</div>;
};

const IndexExplorerPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [inputValue, setInputValue] = useState(params.get('indexName') || '');
  const [getIndexInfo, { loading, data: indexData }] = useIndexInfoQuery();

  const handleSubmit = () => {
    getIndexInfo({ variables: { indexName: inputValue.trim() } });
  };

  useEffect(() => {
    const indexName = new URLSearchParams(location.search).get('indexName');
    if (indexName) {
      getIndexInfo({ variables: { indexName } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const indexResult = get(indexData, 'getIndexInfo', null);
  const command = `aes dqs get-index-info --input-json '{"IndexName": "${inputValue.trim()}"}'`;
  const hasResult = indexResult && Object.keys(indexResult).length > 0;

  const allEntries = hasResult
    ? Object.entries(indexResult).filter(([k]) => k !== '__typename')
    : [];

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Look up index details by Index Name (clientId:datasourceName:indexName)">
        <span style={{ color: '#0972d3' }}>Index Explorer</span>
      </Header>

      <Container>
        <SpaceBetween direction="vertical" size="l">
          <FormField
            label={<strong>Search Index</strong>}
            description="Enter the full Index Name"
            constraintText="Format: clientId:datasourceName:indexName"
          >
            <SpaceBetween direction="horizontal" size="xs">
              <div style={{ width: '600px' }}>
                <Input
                  onChange={({ detail }) => setInputValue(detail.value)}
                  value={inputValue}
                  inputMode="text"
                  placeholder="390403856866:integtestmvds:flint_integtestmvds_default_test_mv_arn_1dcf19"
                  disabled={loading}
                />
              </div>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!inputValue.trim() || loading}
                loading={loading}
              >
                Search
              </Button>
            </SpaceBetween>
          </FormField>
          {inputValue && (
            <Box variant="awsui-key-label">
              CLI Command: <Box variant="code">{command}</Box>
            </Box>
          )}
        </SpaceBetween>
      </Container>

      {loading && (
        <Container>
          <Box textAlign="center" padding={{ vertical: 'xxl' }}>
            <Spinner size="large" />
            <Box variant="p" padding={{ top: 's' }}>Loading index details...</Box>
          </Box>
        </Container>
      )}

      {!loading && hasResult && (
        <SpaceBetween size="l">
          <Container header={<Header variant="h2"><span style={{ color: '#0073bb' }}>Index Information</span></Header>}>
            <ColumnLayout columns={2} variant="text-grid">
              {allEntries.map(([key, value]) => (
                <div key={key}>
                  <Box variant="awsui-key-label">{key}</Box>
                  <ValueDisplay value={value} />
                </div>
              ))}
            </ColumnLayout>
          </Container>

          <ExpandableSection headerText="Full JSON Response" variant="container">
            <CodeViewer
              codeData={JSON.stringify(indexResult, null, 2)}
              language={{ label: 'json', value: 'json' }}
              theme={{ label: 'coy', value: 'coy' }}
            />
          </ExpandableSection>
        </SpaceBetween>
      )}

      {!loading && indexResult && Object.keys(indexResult).length === 0 && (
        <Container>
          <Box textAlign="center" color="inherit">
            <b>No results found for this Index Name</b>
          </Box>
        </Container>
      )}
    </SpaceBetween>
  );
};

export { IndexExplorerPage };
